/**
 * Phase 270c — Reusable Resend sender-domain verification pill.
 *
 * Loads `/super-admin/resend/domain-status` on mount and renders a
 * coloured pill reflecting the live verification state. Used in two
 * places:
 *   • Settings.js → Email Configuration card
 *   • AdoptionNudgeConsole → above the Dispatch button (and the
 *     `onChange` callback wires to disabling Dispatch when unverified)
 *
 * Status colour map:
 *   verified                  → green
 *   pending                   → amber (DNS records added, waiting on Resend probe)
 *   not_started / not_found   → rose (DNS not added yet)
 *   no_api_key                → muted (no creds saved)
 *   anything else             → rose with raw status surfaced
 */
import { useEffect, useState, useCallback } from 'react';
import { CheckCircle2, AlertCircle, Clock, RefreshCw, Loader2 } from 'lucide-react';
import api from '@/lib/api';

const STATUS_TONES = {
  verified: { tone: 'emerald', Icon: CheckCircle2, label: 'Verified' },
  pending: { tone: 'amber', Icon: Clock, label: 'Pending DNS probe' },
  not_started: { tone: 'rose', Icon: AlertCircle, label: 'DNS not added' },
  not_found: { tone: 'rose', Icon: AlertCircle, label: 'Domain not added to Resend' },
  no_api_key: { tone: 'muted', Icon: AlertCircle, label: 'No Resend API key' },
  api_error: { tone: 'rose', Icon: AlertCircle, label: 'Resend API error' },
  transport_error: { tone: 'rose', Icon: AlertCircle, label: 'Network error' },
};

const TONE_CLASSES = {
  emerald: 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
  amber: 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  rose: 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300',
  muted: 'border-border bg-muted/40 text-muted-foreground',
};

export default function ResendDomainPill({ onStatusChange, compact = false, testIdPrefix = 'resend-domain' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStatus = useCallback(async (forceRefresh = false) => {
    if (forceRefresh) setRefreshing(true);
    try {
      const r = await api.get(`/super-admin/resend/domain-status${forceRefresh ? '?refresh=true' : ''}`);
      setData(r.data);
      if (onStatusChange) onStatusChange(r.data);
    } catch (err) {
      setData({
        configured: false,
        sender_status: 'api_error',
        error: err?.response?.data?.detail || 'Failed to fetch domain status',
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [onStatusChange]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  if (loading) {
    return (
      <div className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid={`${testIdPrefix}-loading`}>
        <Loader2 className="h-3 w-3 animate-spin" /> Checking sender domain…
      </div>
    );
  }

  if (!data) return null;
  const meta = STATUS_TONES[data.sender_status] || {
    tone: 'rose', Icon: AlertCircle, label: data.sender_status || 'Unknown',
  };
  const Icon = meta.Icon;

  return (
    <div className="inline-flex items-center gap-2" data-testid={`${testIdPrefix}-pill`}>
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium border rounded-md ${TONE_CLASSES[meta.tone]}`}
        title={data.error || `${data.sender_email} · ${data.sender_status}`}
        data-testid={`${testIdPrefix}-status-${data.sender_status}`}
      >
        <Icon className="h-3 w-3" />
        Sender {data.sender_email || ''} · {meta.label}
      </span>
      {!compact && (
        <button
          type="button"
          onClick={() => fetchStatus(true)}
          disabled={refreshing}
          className="text-[10px] text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
          data-testid={`${testIdPrefix}-refresh`}
          title="Force refresh from Resend (bypasses 5-min cache)"
        >
          <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      )}
      {data.error && !compact && (
        <span className="text-[10px] text-rose-600 dark:text-rose-400" data-testid={`${testIdPrefix}-error-detail`}>
          {data.error}
        </span>
      )}
    </div>
  );
}
